"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isCaseWorkflowBusiness = isCaseWorkflowBusiness;
exports.isCaseDepositMilestone = isCaseDepositMilestone;
exports.getCaseInstantReleasePercent = getCaseInstantReleasePercent;
exports.getFullEscrowMilestoneId = getFullEscrowMilestoneId;
exports.isRenovationSafeguardProject = isRenovationSafeguardProject;
exports.buildCaseInvoiceScheduleRows = buildCaseInvoiceScheduleRows;
exports.resolveLastNonVoMilestoneId = resolveLastNonVoMilestoneId;
exports.resolveCaseInvoiceInstantReleasePercent = resolveCaseInvoiceInstantReleasePercent;
exports.isCaseWorkflowInvoice = isCaseWorkflowInvoice;
exports.resolveCaseWorkflowEnabled = resolveCaseWorkflowEnabled;
exports.getProjectWorkflowKey = getProjectWorkflowKey;
exports.isIdStructureWorkflow = isIdStructureWorkflow;
exports.isCaseTrustBranded = isCaseTrustBranded;
exports.isInteriorDesignEngagement = isInteriorDesignEngagement;
exports.isCaseWorkflowProject = isCaseWorkflowProject;
exports.resolveIsCaseWorkflow = resolveIsCaseWorkflow;
exports.resolveHandshakeServiceFeePercent = resolveHandshakeServiceFeePercent;
const case_workflow_constants_js_1 = require("./case-workflow.constants.js");
const case_deposit_helpers_js_1 = require("./case-deposit.helpers.js");
function isCaseWorkflowBusiness(business) {
    return business?.caseWorkflowEnabled === true;
}
function isCaseDepositMilestone(milestone) {
    if (!milestone)
        return false;
    if (milestone.metadata?.["isCaseDeposit"] === true)
        return true;
    return milestone.name?.trim().toLowerCase() === case_workflow_constants_js_1.CASE_DEPOSIT_MILESTONE_NAME.toLowerCase();
}
function getCaseInstantReleasePercent(invoiceIndex) {
    return invoiceIndex === 0 ? 0 : case_workflow_constants_js_1.CASE_INSTANT_RELEASE_PERCENT_SUBSEQUENT;
}
function getFullEscrowMilestoneId(project) {
    const id = project?.metadata?.[case_workflow_constants_js_1.FULL_ESCROW_MILESTONE_METADATA_KEY];
    return typeof id === "string" && id.trim() ? id : null;
}
/** Renovation Safeguard (`workflow: id`) without CaseTrust branding. */
function isRenovationSafeguardProject(project) {
    return isIdStructureWorkflow(project) && !isCaseTrustBranded({ project });
}
function buildCaseInvoiceScheduleRows(invoices, milestoneById) {
    return invoices.map((invoice) => {
        const milestoneId = invoice.milestoneIds?.[0] ?? "";
        const milestone = milestoneById.get(milestoneId);
        return {
            milestoneId,
            isDeposit: isCaseDepositMilestone(milestone),
            isVariationOrder: (0, case_deposit_helpers_js_1.isCaseVariationOrderMilestone)(milestone),
        };
    });
}
/** Last non-deposit, non-VO row in the invoice schedule (bottom-up). */
function resolveLastNonVoMilestoneId(rows) {
    for (let i = rows.length - 1; i >= 0; i--) {
        const row = rows[i];
        if (!row.milestoneId || row.isDeposit || row.isVariationOrder)
            continue;
        return row.milestoneId;
    }
    return null;
}
function resolveCaseInvoiceInstantReleasePercent(options) {
    if (isCaseDepositMilestone(options.milestone)) {
        return 0;
    }
    if ((0, case_deposit_helpers_js_1.isCaseVariationOrderMilestone)(options.milestone)) {
        return case_workflow_constants_js_1.CASE_INSTANT_RELEASE_PERCENT_SUBSEQUENT;
    }
    if (options.isRenovationSafeguard &&
        options.fullEscrowMilestoneId &&
        options.milestoneId === options.fullEscrowMilestoneId) {
        return 0;
    }
    return case_workflow_constants_js_1.CASE_INSTANT_RELEASE_PERCENT_SUBSEQUENT;
}
function isCaseWorkflowInvoice(invoice) {
    return invoice?.instantReleasePercent != null;
}
function resolveCaseWorkflowEnabled(body) {
    return body.caseWorkflowEnabled === true || body.signupSource === "case-flow";
}
function getProjectWorkflowKey(project) {
    const workflow = project?.metadata?.["workflow"];
    return typeof workflow === "string" ? workflow : null;
}
/** Interior Design structure flag on the project (no CaseTrust brand). */
function isIdStructureWorkflow(project) {
    return getProjectWorkflowKey(project) === case_workflow_constants_js_1.ID_STRUCTURE_METADATA_KEY;
}
/**
 * CaseTrust accreditation / branding — business flag or project stamped as `workflow: "case"`.
 * Does **not** include Interior Design (`workflow: "id"`).
 */
function isCaseTrustBranded(options) {
    const business = options?.business ?? options?.project?.business;
    if (isCaseWorkflowBusiness(business))
        return true;
    return getProjectWorkflowKey(options?.project) === case_workflow_constants_js_1.CASE_WORKFLOW_METADATA_KEY;
}
/**
 * Interior Design (ID) company or project — used to show ID-only content (e.g. the Aces Programme
 * defect assessment step in the dispute guide, HAN-633) to ID businesses and their clients only.
 * True when the business's industry is Interior Design, the business is CaseTrust-accredited
 * (CaseTrust only accredits ID firms), or the project uses the CaseTrust / ID payment structure
 * (`workflow: "case" | "id"`, which is only offered to ID businesses).
 */
function isInteriorDesignEngagement(options) {
    const business = options?.business ?? options?.project?.business;
    if (business?.industry === case_workflow_constants_js_1.INTERIOR_DESIGN_INDUSTRY)
        return true;
    return isCaseWorkflowProject({ metadata: options?.project?.metadata, business });
}
/**
 * Payment structure (deposit + 50/50) — CaseTrust business, `workflow: "case"|"id"`, or case invoice.
 */
function isCaseWorkflowProject(project) {
    if (isCaseWorkflowBusiness(project?.business))
        return true;
    const workflow = getProjectWorkflowKey(project);
    return workflow === case_workflow_constants_js_1.CASE_WORKFLOW_METADATA_KEY || workflow === case_workflow_constants_js_1.ID_STRUCTURE_METADATA_KEY;
}
/** Unified structure detection for drawdown, escrow display, and validation. */
function resolveIsCaseWorkflow(options) {
    if (isCaseWorkflowBusiness(options?.business))
        return true;
    if (isCaseWorkflowProject(options?.project))
        return true;
    return isCaseWorkflowInvoice(options?.invoice);
}
function resolveHandshakeServiceFeePercent(options) {
    const projectFee = options?.project?.handshakeServiceFeePercent;
    if (projectFee != null && !Number.isNaN(Number(projectFee))) {
        return Number(projectFee);
    }
    const business = options?.business ?? options?.project?.business;
    const businessDefault = business?.defaultHandshakeServiceFeePercent;
    if (businessDefault != null && !Number.isNaN(Number(businessDefault))) {
        return Number(businessDefault);
    }
    // 0.9% only for CaseTrust-branded; ID structure uses Handshake default.
    if (isCaseTrustBranded({ business, project: options?.project })) {
        return case_workflow_constants_js_1.CASE_DEFAULT_SERVICE_FEE_PERCENT;
    }
    return case_workflow_constants_js_1.HANDSHAKE_DEFAULT_SERVICE_FEE_PERCENT;
}
